import {
  COMPANY_ADDRESS_FULL,
  COMPANY_LOCATION,
  COMPANY_NAME,
  COMPANY_SHORT,
  HERO_IMAGE_URL,
  MAP_URL,
  COMMUNITY_FOCUS,
  PROFILE_FACTS,
  PUBLIC_INFO,
} from './constants';

function Header() {
  const navItems = [
    { label: 'Community', href: '#community' },
    { label: 'Voice', href: '#voice' },
    { label: 'Visit', href: '#visit' },
  ];

  return (
    <header className="sticky top-0 z-30 bg-cream/85 backdrop-blur">
      <div className="mx-auto flex max-w-6xl flex-col items-center gap-3 px-5 py-5 sm:flex-row sm:justify-between">
        <a href="#top" className="flex items-center gap-3" aria-label={`${COMPANY_SHORT} home`}>
          <span className="grid h-11 w-11 place-items-center rounded-full bg-forest text-xs font-black uppercase tracking-wider text-cream">
            RA
          </span>
          <span className="font-serif text-lg leading-none text-forest sm:text-xl">{COMPANY_SHORT}</span>
        </a>
        <nav
          className="flex items-center gap-1 rounded-full border border-edge bg-surface/70 px-1.5 py-1 text-sm font-semibold text-bark/75"
          aria-label="Primary"
        >
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="rounded-full px-4 py-2 transition hover:bg-forest hover:text-cream"
            >
              {item.label}
            </a>
          ))}
        </nav>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section id="top" className="relative overflow-hidden bg-cream">
      <div className="pointer-events-none absolute -right-32 -top-24 h-80 w-80 rounded-full bg-sage/40 blur-3xl" />
      <div className="pointer-events-none absolute -left-24 bottom-0 h-64 w-64 rounded-full bg-terracotta/20 blur-3xl" />
      <div className="relative mx-auto max-w-6xl px-5 py-20 text-center lg:py-28">
        <span className="inline-flex items-center gap-2 rounded-full border border-forest/20 bg-surface/70 px-4 py-1.5 text-xs font-black uppercase tracking-[0.22em] text-forest">
          <span className="h-1.5 w-1.5 rounded-full bg-terracotta" />
          Residents-led · Public profile
        </span>
        <h1 className="mx-auto mt-7 max-w-4xl font-serif text-4xl leading-[1.05] text-bark sm:text-5xl lg:text-6xl">
          {COMPANY_NAME}
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-stone">
          A warm, public-safe profile for the residents association of Friendship Village of Dublin — written in the
          spirit of a friendly front porch rather than an institutional brochure.
        </p>
        <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <a
            href="#community"
            className="rounded-full bg-forest px-6 py-3 font-bold text-cream shadow-warm transition hover:bg-moss"
          >
            Meet the community
          </a>
          <a
            href="#visit"
            className="rounded-full border border-forest/30 bg-surface px-6 py-3 font-bold text-forest transition hover:border-forest hover:bg-linen"
          >
            See the address
          </a>
        </div>

        <div className="mt-16 overflow-hidden rounded-soft border border-edge shadow-warm">
          <div className="relative h-72 sm:h-96">
            <img
              src={HERO_IMAGE_URL}
              alt="Friendship Village residents community visual"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-bark/70 via-bark/10 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-6 text-left text-cream">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-amber">Friendship Village · Dublin</p>
              <p className="mt-1 font-serif text-2xl">{COMPANY_LOCATION}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FactsGrid() {
  return (
    <section aria-label="Profile facts" className="bg-linen">
      <div className="mx-auto grid max-w-6xl gap-4 px-5 py-12 sm:grid-cols-3">
        {PROFILE_FACTS.map((fact) => (
          <div
            key={fact.label}
            className="rounded-soft border border-edge bg-surface px-6 py-7 shadow-ring transition hover:-translate-y-0.5 hover:shadow-warm"
          >
            <p className="text-xs font-black uppercase tracking-[0.2em] text-terracotta">{fact.label}</p>
            <p className="mt-3 font-serif text-xl leading-snug text-forest">{fact.value}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function SectionHeading({ eyebrow, title, children, center }) {
  return (
    <div className={center ? 'mx-auto max-w-2xl text-center' : 'max-w-2xl'}>
      <p className="text-xs font-black uppercase tracking-[0.22em] text-terracotta">{eyebrow}</p>
      <h2 className="mt-3 font-serif text-3xl leading-tight text-bark sm:text-4xl">{title}</h2>
      {children ? <p className="mt-4 leading-7 text-stone">{children}</p> : null}
    </div>
  );
}

function VoiceSection() {
  return (
    <section id="voice" className="bg-cream">
      <div className="mx-auto max-w-6xl px-5 py-20">
        <SectionHeading center eyebrow="Residents Voice" title="A neighborly profile, not a corporate page">
          The residents association is presented in plain language: who they are, where they meet, and the public
          mailing address used for friendly correspondence.
        </SectionHeading>
        <figure className="mx-auto mt-12 max-w-4xl rounded-soft border border-edge bg-surface p-10 shadow-warm">
          <p className="font-serif text-2xl leading-relaxed text-bark sm:text-3xl">
            “A residents association is at its best when it feels like a porch conversation — open, neighborly, and
            grounded in the place we all call home.”
          </p>
          <figcaption className="mt-6 flex items-center gap-3 text-sm font-bold uppercase tracking-[0.18em] text-forest">
            <span className="h-px w-10 bg-terracotta" />
            Residents Association · Friendship Village of Dublin
          </figcaption>
        </figure>
      </div>
    </section>
  );
}

function CommunitySection() {
  return (
    <section id="community" className="bg-linen">
      <div className="mx-auto max-w-6xl px-5 py-20">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading eyebrow="Community Focus" title="Friendship, hospitality, shared porches">
            The layout borrows from community-zine design: rounded surfaces, warm color, and a friendly timeline
            instead of formal feature cards.
          </SectionHeading>
          <p className="max-w-sm text-sm font-bold uppercase tracking-[0.18em] text-stone">
            Residents-led · Public-safe · Source-backed
          </p>
        </div>

        <ol className="relative mt-12 space-y-6 border-l-2 border-dashed border-sage pl-8">
          {COMMUNITY_FOCUS.map((note) => (
            <li key={note.title} className="relative">
              <span className="absolute -left-[2.45rem] grid h-11 w-11 place-items-center rounded-full border-2 border-sage bg-cream font-black text-forest">
                {note.number}
              </span>
              <div className="rounded-soft border border-edge bg-surface p-6 shadow-ring">
                <h3 className="font-serif text-xl text-bark">{note.title}</h3>
                <p className="mt-3 leading-7 text-stone">{note.body}</p>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-12 grid gap-4 sm:grid-cols-3">
          {PUBLIC_INFO.map((item) => (
            <div key={item.label} className="rounded-soft bg-forest p-6 text-cream">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-amber">{item.label}</p>
              <p className="mt-3 font-serif text-lg leading-snug">{item.value}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function VisitBand() {
  return (
    <section id="visit" className="bg-terracotta text-cream">
      <div className="mx-auto grid max-w-6xl gap-10 px-5 py-20 lg:grid-cols-[1fr_1.25fr] lg:items-center">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.22em] text-amber">Visit · Mail · Find</p>
          <h2 className="mt-3 font-serif text-3xl leading-tight sm:text-4xl">
            Friendship Village of Dublin
          </h2>
          <p className="mt-5 max-w-md leading-7 text-cream/85">
            The address below is the public-facing reference for the residents association. Drop a friendly letter,
            plan a visit, or pin the location for community correspondence.
          </p>
        </div>
        <div className="rounded-soft bg-cream p-8 text-bark shadow-warm">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-terracotta">Public mailing address</p>
          <p className="mt-4 font-serif text-2xl leading-snug text-forest">{COMPANY_ADDRESS_FULL}</p>
          <a
            href={MAP_URL}
            target="_blank"
            rel="noreferrer"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-forest px-6 py-3 font-bold text-cream transition hover:bg-moss"
          >
            Open in map
            <span aria-hidden="true">→</span>
          </a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-forest text-cream/75">
      <div className="mx-auto flex max-w-6xl flex-col gap-3 px-5 py-10 text-sm md:flex-row md:items-center md:justify-between">
        <p className="font-bold text-cream">
          &copy; {new Date().getFullYear()} {COMPANY_NAME}
        </p>
        <p className="font-semibold tracking-wide">{COMPANY_LOCATION}</p>
      </div>
    </footer>
  );
}

function App() {
  return (
    <div className="min-h-screen bg-cream text-bark">
      <a
        href="#community"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded-full focus:bg-forest focus:px-4 focus:py-2 focus:text-cream"
      >
        Skip to content
      </a>
      <Header />
      <main>
        <Hero />
        <FactsGrid />
        <VoiceSection />
        <CommunitySection />
        <VisitBand />
      </main>
      <Footer />
    </div>
  );
}

export default App;
